import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { useId } from 'react'

/**
 * Form primitives for the protocol editor.
 *
 * Sized for a thumb, not a mouse: inputs are 44px tall, which is the smallest
 * comfortable touch target and still looks right on a desktop form. The same
 * components serve both contexts rather than branching on viewport.
 */

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    // `children` IS the control — every caller passes a TextInput, TextArea or
    // Segmented. The rule cannot see through the prop.
    // biome-ignore lint/a11y/noLabelWithoutControl: see above
    <label className="block">
      <span className="text-muted mb-1 block text-[11px] tracking-wide uppercase">{label}</span>
      {children}
      {hint && <span className="text-faint mt-1 block text-[11px]">{hint}</span>}
    </label>
  )
}

/** The control look, exported for the odd control these components do not cover. */
export const CONTROL =
  'border-line focus:border-brand bg-surface text-ink placeholder:text-faint w-full border px-3 py-2.5 text-sm transition-colors outline-none disabled:opacity-60'

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${props.className ?? ''}`} />
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} rows={props.rows ?? 3} className={`${CONTROL} resize-y`} />
}

/** Segmented control — faster than a select on a phone and shows every option. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T
  options: { value: T; label: string; tone?: 'default' | 'warn' }[]
  onChange: (value: T) => void
  disabled?: boolean
}) {
  return (
    <div className="border-line flex flex-wrap border">
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={`flex-1 px-3 py-2.5 text-xs whitespace-nowrap transition-colors disabled:opacity-60 ${
              active
                ? option.tone === 'warn'
                  ? 'bg-danger text-surface font-medium'
                  : 'bg-brand text-surface font-medium'
                : 'text-muted hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** Numeric stepper. Typing a key count on a phone keyboard is needless friction. */
export function Counter({
  value,
  onChange,
  disabled,
}: {
  value: number
  onChange: (value: number) => void
  disabled?: boolean
}) {
  const id = useId()

  return (
    <div className="border-line flex items-stretch border">
      <button
        type="button"
        disabled={disabled || value <= 0}
        onClick={() => onChange(Math.max(0, value - 1))}
        aria-label="weniger"
        aria-controls={id}
        className="text-muted hover:text-ink hover:bg-canvas w-11 text-lg transition-colors disabled:opacity-30"
      >
        −
      </button>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Math.max(0, Number(event.target.value) || 0))}
        className="tnum border-line text-ink w-14 border-x bg-transparent py-2.5 text-center font-mono text-sm outline-none"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(value + 1)}
        aria-label="mehr"
        aria-controls={id}
        className="text-muted hover:text-ink hover:bg-canvas w-11 text-lg transition-colors disabled:opacity-30"
      >
        +
      </button>
    </div>
  )
}

/** A removable card for one repeatable entry (meter, key, room, defect). */
export function EntryCard({
  title,
  onRemove,
  removeLabel = 'Entfernen',
  removeAriaLabel,
  children,
  tone,
}: {
  title: ReactNode
  onRemove?: () => void
  removeLabel?: string
  /**
   * Accessible name for the remove button, when the visible "Entfernen" is not
   * enough — six cards read out as six identical "Entfernen" buttons otherwise.
   */
  removeAriaLabel?: string
  children: ReactNode
  tone?: 'default' | 'flagged'
}) {
  return (
    <section
      className={`border-line bg-surface border p-3 sm:p-4 ${
        tone === 'flagged' ? 'border-l-danger border-l-2' : ''
      }`}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">{title}</div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={removeAriaLabel}
            // The padding is cancelled by the negative margin: a thumb-sized
            // hit area around a small text link, with no change to the layout.
            className="text-faint hover:text-danger -m-2 shrink-0 p-2 text-xs underline underline-offset-2"
          >
            {removeLabel}
          </button>
        )}
      </header>
      {children}
    </section>
  )
}

export function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-line text-muted hover:border-brand hover:text-brand w-full border border-dashed px-3 py-2.5 text-xs transition-colors"
    >
      + {children}
    </button>
  )
}
